import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LogIn, SlidersHorizontal, MonitorPlay } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { DownloadButton } from '@/components/marketing/DownloadButton';
import { RouteHead } from '@/components/seo/RouteHead';
import { ROUTES } from '@/routes';
import { fadeUp, fadeIn, staggerContainer, VIEWPORT_REVEAL, STAGGER } from '@/lib/motion';

type LucideIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface StepProps {
  icon: LucideIcon;
  step: string;
  title: string;
  description: string;
  detail_1: string;
  detail_2: string;
  detail_3: string;
  isLast?: boolean;
}

function Step({ icon: Icon, step, title, description, detail_1, detail_2, detail_3, isLast }: StepProps) {
  const details = [detail_1, detail_2, detail_3];
  return (
    <motion.div
      variants={staggerContainer(STAGGER.base)}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_REVEAL}
      className="relative flex gap-8"
    >
      {!isLast && (
        <div
          className="absolute start-9 top-20 bottom-0 w-px bg-border"
          aria-hidden="true"
        />
      )}

      <motion.div variants={fadeIn} className="relative z-10 shrink-0">
        <div className="flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl border border-brand/40 bg-primary-soft shadow-soft">
          <Icon className="h-6 w-6 text-brand" aria-hidden="true" />
        </div>
      </motion.div>

      <motion.div variants={staggerContainer(STAGGER.base)} className="flex flex-col gap-4 pb-16">
        <motion.p
          variants={fadeIn}
          className="font-mono text-xs font-semibold uppercase tracking-widest text-brand"
        >
          {step}
        </motion.p>
        <motion.div variants={fadeUp}>
          <Heading level={3}>{title}</Heading>
        </motion.div>
        <motion.p variants={fadeUp} className="text-muted-foreground">
          {description}
        </motion.p>
        <motion.ul variants={staggerContainer(STAGGER.tight)} className="flex flex-col gap-2">
          {details.map((detail) => (
            <motion.li
              key={detail}
              variants={fadeUp}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <span
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand"
                aria-hidden="true"
              />
              {detail}
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </motion.div>
  );
}

export default function HowItWorksPage() {
  const { t } = useTranslation('how-it-works');

  const steps: Omit<StepProps, 'isLast'>[] = [
    {
      icon: LogIn,
      step: t('steps.connect.step'),
      title: t('steps.connect.title'),
      description: t('steps.connect.description'),
      detail_1: t('steps.connect.detail_1'),
      detail_2: t('steps.connect.detail_2'),
      detail_3: t('steps.connect.detail_3'),
    },
    {
      icon: SlidersHorizontal,
      step: t('steps.configure.step'),
      title: t('steps.configure.title'),
      description: t('steps.configure.description'),
      detail_1: t('steps.configure.detail_1'),
      detail_2: t('steps.configure.detail_2'),
      detail_3: t('steps.configure.detail_3'),
    },
    {
      icon: MonitorPlay,
      step: t('steps.join.step'),
      title: t('steps.join.title'),
      description: t('steps.join.description'),
      detail_1: t('steps.join.detail_1'),
      detail_2: t('steps.join.detail_2'),
      detail_3: t('steps.join.detail_3'),
    },
  ];

  return (
    <>
      <RouteHead
        path={ROUTES.howItWorks}
        titleKey="seo:how_it_works.title"
        descriptionKey="seo:how_it_works.description"
      />
      {/* Hero */}
      <Section padding="lg">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              variants={fadeIn}
              className="mb-4 text-xs font-semibold uppercase tracking-widest text-brand"
            >
              {t('page.title')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Heading level={1}>{t('hero.headline')}</Heading>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              {t('hero.subheadline')}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* Steps */}
      <Section padding="lg" bordered>
        <Container size="md">
          <div className="flex flex-col">
            {steps.map((step, idx) => (
              <Step key={step.step} {...step} isLast={idx === steps.length - 1} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Under the hood */}
      <Section padding="lg" className="bg-card">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('how_section.title')}</Heading>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-4 max-w-2xl text-muted-foreground"
            >
              {t('how_section.description')}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* CTA */}
      <Section padding="lg" className="bg-primary-soft">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('cta.headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-md text-muted-foreground">
              {t('cta.subheadline')}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex justify-center">
              <DownloadButton />
            </motion.div>
          </motion.div>
        </Container>
      </Section>
    </>
  );
}
